import axios from 'axios';
import {QueryClient, type QueryClientConfig} from '@tanstack/react-query';
import {session} from '@netx/core-auth/collections/singleton/session';
import {dispatcher} from '../instance/dispatcher';
import {SortOrder} from '../schema';
import {URL} from '../url';

// react-query client config
const queryConfig: QueryClientConfig = {
	defaultOptions: {
		queries: {
			networkMode: 'always',
			refetchOnMount: false,
			refetchOnWindowFocus: false,
			retry: false,
		},
		mutations: {
			networkMode: 'always',
			retry: false,
		},
	},
};

// react-query client instance
export const queryClient = new QueryClient(queryConfig);

/**
 * Server error
 * @typeParam Codes - Error codes expected from the server
 */
export type ErrorResult<Codes extends number = number> = {
	code: Codes;
	data?: null;
	message: string;
	result: string;
};

/**
 * Server error response
 * @typeParam T - Error type
 * @see ErrorResult
 */
export type ErrorResponse<T extends ErrorResult = ErrorResult> = {
	error: T;
};

/**
 * Server success response
 * @typeParam T - Result type
 */
export type SuccessResponse<T> = {
	result: T;
};

/**
 * Query error
 * @typeParam Codes - Error codes expected from the server
 */
export class QueryError<Codes extends number = number> extends Error {
	code: Codes = -1 as Codes;
}

/**
 * Server paging param (`PageDTO`)
 */
export type QueryPager = {
	startIndex: number;
	size: number;
};

/**
 * Server sort param (`SortDTO`)
 */
export type QuerySort<F extends string = string> = {
	field: F;
	order: SortOrder;
};

// These are the error codes that we want to handle as session errors
const sessionErrors = [6, 3001, 3002, 3003];

interface QueryPromise<T = any> extends Promise<T> {
	cancel(): void;
}

/**
 * Netx query client.\
 * Wrapper around axios to handle, session, session errors, and cancelation.
 *
 * @typeParam ErrorCodes - Error codes expected from the server
 *
 * @returns Wrapped axios client
 *
 * @example
 * const client = netxQueryClient<2102 | 1000>();
 * client.post('method', data).then((response) => {...});
 */
export const netxQueryClient = <ErrorCodes extends number = number>() => {
	const client = axios.create({
		baseURL: URL.api,
	});
	client.defaults.headers.common['Content-Type'] = 'application/json';

	const source = axios.CancelToken.source();

	// Back it up
	const _post = client.post;
	// And wrap it
	// We can ignore ts check on this, because as long as we do not modify the response
	// data, we will still be getting proper types out
	// For some reason, assigning the _post call to a var and returning is not valid but
	// just returning the _post call is valid (something in the abstract types)
	// @ts-ignore
	client.post = (method, data = [], config = {}) => {
		// every succesful sync resets the server's logout timer; reset our client one too.
		session.resetLogoutTimer();

		if (!(session?.get('publicMethods') || []).includes(method)) {
			(data as unknown as any[]).unshift(
				session.getToken() || session.getKey(),
			);
		}

		const promise = _post(
			method,
			{
				id: Date.now(),
				dataContext: 'json',
				jsonrpc: '2.0',
				method,
				params: data,
			} as unknown,
			{...config, cancelToken: source.token},
		).then((response) => {
			if (response.data.error as ErrorResponse) {
				const code = response.data.error.code;
				if (sessionErrors.includes(code)) {
					// If we have a token, some calls with give sessionErrors while others will pass.
					// If not, a session error means our key is invalid.
					if (!session?.getToken()) dispatcher.trigger('session:expire');
				}
				const error = new QueryError<ErrorCodes>(response.data.error.message);
				error.code = code;
				throw error;
			}

			session?.resetLogoutTimer();

			return response;
		});

		// For react query to cancel on unmount (or any other to cancel via the promise)
		// TODO: soon we can remove this as axios now supports passing signal
		// just need to hunt down anything depending on this behvior and pass along the signal
		// from react query
		(promise as QueryPromise).cancel = () => {
			return source.cancel('cancelled by external script');
		};

		return promise;
	};

	return client;
};

export default netxQueryClient;
